// Typed API client (Phase 4.5 deliverable #7).
//
// Wraps the `app` process's TDD §4 endpoint set:
//   * GET  /health
//   * POST /api/v1/approvals/request   (TDD §4.1.1)
//   * POST /api/v1/approvals/confirm   (TDD §4.1.2)
//   * GET  /api/v1/risk/snapshot       (TDD §4.1.3)
//   * POST /api/v1/config/change       (TDD §4.1.4)
//
// Auth: the `app` process's session-cookie middleware reads
// `tradingplatform_session` from the cookie jar. The UI's login
// screen sets the cookie (Phase 4.5 D7 placeholder for the
// production /login endpoint). Once set, fetch() includes the
// cookie automatically because the UI is same-origin with the
// app process (both bind 127.0.0.1).
//
// Error path: on a non-2xx response, `apiCall` parses the body
// as RFC 7807 problem-details and throws a `ProblemDetailError`.
// Callers can `instanceof` check to surface the type/title/
// detail/instance fields to the user.

// Default base URL.
//
// Production: empty string -> all calls are same-origin relative
// to the page that loaded the bundle (e.g. https://192.168.12.168/).
// nginx proxies /api/* to the gateway on 127.0.0.1:8080.
//
// Dev: when the Vite dev server (on :5173) needs to talk to a real
// backend on :8080, set VITE_API_BASE_URL=http://127.0.0.1:8080 in
// the environment. (Fixtures mode bypasses the API entirely.)
import { z } from "zod";

const DEFAULT_BASE_URL: string =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "";

// ---------------------------------------------------------------------
// RFC 7807 problem-details + error class
// ---------------------------------------------------------------------

export type ProblemDetail = {
  type: string;
  title: string;
  status: number;
  detail: string;
  instance: string;
};

export class ProblemDetailError extends Error {
  readonly type: string;
  readonly title: string;
  readonly status: number;
  readonly detail: string;
  readonly instance: string;

  constructor(problem: ProblemDetail) {
    super(`${problem.title}${problem.detail ? `: ${problem.detail}` : ""}`);
    this.name = "ProblemDetailError";
    this.type = problem.type;
    this.title = problem.title;
    this.status = problem.status;
    this.detail = problem.detail;
    this.instance = problem.instance;
  }
}

// ---------------------------------------------------------------------
// Runtime response validation (2026-06-10 safety review, finding 6).
//
// Money-path wire types are validated with zod at the call boundary so
// a malformed/renamed backend field surfaces as a structured
// ProblemDetailError instead of silently feeding NaN into risk math.
//
// PATTERN FOR EXTENSION: other endpoints currently keep the plain
// `as T` cast in `call<T>()`. To bring an endpoint under runtime
// validation, define a `z.looseObject` schema for its wire type below
// (loose, so additive backend fields never break the UI) and wrap the
// call result with `validateWire(path, schema, raw)`.
// ---------------------------------------------------------------------

const tradePreviewWireSchema = z.looseObject({
  derived_qty: z.string(),
  actual_risk_dollars: z.string(),
  target_gain_dollars: z.string(),
  risk_reward_ratio: z.string(),
  basis_price: z.string(),
  stop_price: z.string(),
  target_price: z.string(),
  risk_per_share: z.string(),
  gain_per_share: z.string(),
});

const pendingApprovalWireSchema = z.looseObject({
  approval_id: z.string(),
  broker_account_id: z.string(),
  account_type: z.string().nullish(),
  asset_class: z.string().nullish(),
  symbol: z.string(),
  company_name: z.string().nullish(),
  // `side` drives long/short sign logic — keep it strict.
  side: z.enum(["buy", "sell"]),
  sized_shares: z.string(),
  sized_entry_price: z.string(),
  sized_stop_price: z.string().nullable(),
  sized_target_prices: z.array(z.string()),
  account_equity_snapshot: z.string(),
  timeframe: z.string().nullish(),
  signal_metadata: z.record(z.string(), z.unknown()).optional(),
  agent_review: z.unknown().nullish(),
  requested_at: z.string(),
  token_expires_at: z.string(),
});

const approvalReviewResponseSchema = z.looseObject({
  approval_id: z.string(),
  actionable: z.boolean(),
  checked_at: z.string(),
  current_price: z.string().nullable(),
  price_drift_pct: z.string().nullable(),
  preview: tradePreviewWireSchema.nullish(),
  current_price_preview: tradePreviewWireSchema.nullish(),
  issues: z.array(
    z.looseObject({ code: z.string(), detail: z.string() }),
  ),
});

const confirmAndPlaceResponseSchema = z.looseObject({
  outcome: z.literal("placed"),
  approval_id: z.string(),
  consumed_at: z.string(),
  broker_orders: z.array(
    z.looseObject({
      order_id: z.string(),
      // Lenient strings: new leg types / broker statuses must not
      // reject an order that was actually placed.
      leg_type: z.string(),
      status: z.string(),
    }),
  ),
});

const accountWireSchema = z.looseObject({
  broker_account_id: z.string(),
  label: z.string(),
  broker: z.string(),
  mask: z.string(),
  equity: z.string(),
  buying_power: z.string(),
  cash: z.string(),
  today_pnl: z.string(),
  pattern_day_trader: z.boolean(),
  trading_blocked: z.boolean(),
  asset_class: z.string().optional(),
  account_type: z.string().optional(),
});

const accountsWireSchema = z.array(accountWireSchema);
const pendingApprovalsWireSchema = z.array(pendingApprovalWireSchema);
const strategyRouteTargetWireSchema = z.looseObject({
  broker_account_id: z.string(),
  account_type: z.string(),
  asset_class: z.string(),
  label: z.string(),
  enabled: z.boolean(),
  blocked_reason: z.string().nullable(),
});
const strategyRouteWireSchema = z.looseObject({
  strategy_id: z.string(),
  base_strategy_id: z.string(),
  name: z.string(),
  version: z.string(),
  timeframe: z.string(),
  status: z.string(),
  broker_account_id: z.string(),
  account_type: z.string(),
  asset_class: z.string(),
  account_label: z.string(),
  route_targets: z.array(strategyRouteTargetWireSchema),
});
const strategyRoutesWireSchema = z.array(strategyRouteWireSchema);

function validateWire<T>(
  path: string,
  schema: z.ZodType,
  raw: unknown,
): T {
  const result = schema.safeParse(raw);
  if (!result.success) {
    const summary = result.error.issues
      .slice(0, 3)
      .map((issue) => `${issue.path.join(".") || "<root>"}: ${issue.message}`)
      .join("; ");
    throw new ProblemDetailError({
      type: "https://tradingplatform.local/errors/invalid-response",
      title: "Invalid API response",
      status: 502,
      detail:
        `response from ${path} did not match the expected wire shape ` +
        `(${summary}). Refusing to display unverified money data.`,
      instance: path,
    });
  }
  // zod's parsed output is structurally the wire type; the cast is the
  // same one `call<T>` made, now guarded by the schema check above.
  return result.data as T;
}

// ---------------------------------------------------------------------
// Wire types — mirror the Pydantic shapes on the app process
// ---------------------------------------------------------------------

export type SubsystemStatusWire = {
  name: string;
  status: "ok" | "degraded" | "down";
  detail: string;
};

export type HealthResponse = {
  // Phase 5 (b) deliverable b.2 expanded the status union to
  // include "down" + added per-subsystem reports per UX §14.1.
  status: "ok" | "degraded" | "down";
  process: "app";
  subsystems: SubsystemStatusWire[];
};

export type ApprovalRequestBody = {
  broker_account_id: string; // UUID
  symbol: string;
  side: "buy" | "sell";
  sized_shares: number | string;
  sized_entry_price: string; // Decimal-as-string
  sized_stop_price?: string | null;
  sized_target_prices?: string[];
  account_equity_snapshot?: string;
};

export type ApprovalResponse = {
  approval_id: string;
  broker_account_id: string;
  symbol: string;
  side: "buy" | "sell";
  sized_shares: string;
  sized_entry_price: string;
  sized_stop_price: string | null;
  sized_target_prices: string[];
  requested_at: string;
  token_expires_at: string;
};

export type ApprovalReviewIssueWire = {
  code: string;
  detail: string;
};

export type TradePreviewWire = {
  derived_qty: string;
  actual_risk_dollars: string;
  target_gain_dollars: string;
  risk_reward_ratio: string;
  basis_price: string;
  stop_price: string;
  target_price: string;
  risk_per_share: string;
  gain_per_share: string;
};

export type ApprovalReviewResponse = {
  approval_id: string;
  broker_account_id?: string;
  symbol?: string;
  side?: "buy" | "sell";
  actionable: boolean;
  checked_at: string;
  current_price: string | null;
  price_drift_pct: string | null;
  preview?: TradePreviewWire | null;
  current_price_preview?: TradePreviewWire | null;
  issues: ApprovalReviewIssueWire[];
};

export type ConfirmRequestBody = {
  approval_id: string;
  idempotency_key: string;
  user_acknowledged_risk?: boolean;
  user_acknowledged_gap_risk?: boolean;
  // Real-money ack (UX §8.7). Server requires it True for live
  // confirm / confirm-and-place; omit/false for paper.
  user_acknowledged_live_real_money?: boolean;
};

export type ApprovalRepriceRequestBody = {
  signal_id?: string | null;
  symbol: string;
  strategy_id: string;
  bar_date?: string | null;
  direction: "long" | "short";
  entry_price: string;
  stop_price: string;
  target_prices: string[];
  rr_ratio?: string | null;
  score?: number | null;
  factors?: string[];
  ai_confidence?: number | null;
  ai_rationale?: string | null;
  ai_risk_flags?: string[];
  ai_model_id?: string | null;
  risk_dollars?: string | null;
};

export type ApprovalRepriceResponse = {
  approval: ApprovalResponse;
  source_entry_price: string;
  current_price: string;
  reward_r_multiple: string;
  preview?: TradePreviewWire | null;
};

export type ApprovalPreviewResponse = {
  symbol: string;
  side: "buy" | "sell";
  broker_account_id: string;
  source_entry_price: string;
  current_price: string;
  stop_price: string;
  target_price: string;
  reward_r_multiple: string;
  preview: TradePreviewWire;
  actionable: boolean;
  checked_at: string;
  issues: ApprovalReviewIssueWire[];
};

export type ApprovalResizeResponse = {
  approval: ApprovalResponse;
  preview: TradePreviewWire;
  checked_at: string;
};

export type ConfirmResponse = {
  outcome: "accepted" | "rejected";
  approval_id: string;
  broker_token: string | null;
  consumed_at: string | null;
  rejection_status_code: 409 | 410 | 422 | null;
  rejection_reason: string | null;
  rejection_detail: string | null;
};

export type ConfirmAndPlaceResponse = {
  outcome: "placed";
  approval_id: string;
  consumed_at: string;
  broker_orders: {
    order_id: string;
    leg_type: "entry" | "stop" | "target";
    status: "working" | "filled" | "canceled" | "rejected";
  }[];
  history_recorded?: boolean;
  history_error?: string | null;
};

export type RiskSnapshotResponse = {
  broker_account_id: string;
  // Plus arbitrary additional fields per the app's
  // RiskSnapshotResponseModel (extra="allow").
  [key: string]: unknown;
};

export type ConfigChangeRequestBody = {
  field_name: string;
  old_value: string;
  new_value: string;
  reflection_text?: string;
};

export type ConfigChangeResponse = {
  field_name: string;
  status: "applied" | "queued" | "rejected";
  // Plus arbitrary additional fields.
  [key: string]: unknown;
};

export type CoolingOffConfigWire = {
  cooling_off_days: number;
  updated_at: string | null;
  updated_by: string | null;
};

export type CoolingOffConfigUpdateBody = {
  cooling_off_days: number;
};

export type AutoApprovalsConfigWire = {
  enabled: boolean;
  min_confidence: number;
  updated_at: string | null;
  updated_by: string | null;
};

export type AutoApprovalsConfigUpdateBody = {
  enabled: boolean;
  min_confidence: number;
};

export type StrategyRouteTargetWire = z.infer<
  typeof strategyRouteTargetWireSchema
>;

export type StrategyRouteWire = z.infer<typeof strategyRouteWireSchema>;

export type StrategyRouteUpdateBody = {
  broker_account_id: string;
  operator_note?: string | null;
};

export type TelegramAgentAuthorityMode = "notify_only" | "two_step_confirm";

export type TelegramAgentConfigWire = {
  enabled: boolean;
  authority_mode: TelegramAgentAuthorityMode;
  allowed_chat_ids: string[];
  allowed_user_ids: string[];
  allowed_broker_account_ids: string[];
  notification_categories: string[];
  updated_at: string | null;
  updated_by: string | null;
};

export type TelegramAgentConfigUpdateBody = {
  enabled: boolean;
  authority_mode: TelegramAgentAuthorityMode;
  allowed_chat_ids: string[];
  allowed_user_ids: string[];
  allowed_broker_account_ids: string[];
  notification_categories: string[];
};

export type ReassessmentSeverityWire = "low" | "medium" | "high";

export type PositionReassessmentConfigWire = {
  enabled: boolean;
  cadence_minutes: number;
  notification_min_severity: ReassessmentSeverityWire;
  updated_at: string | null;
  updated_by: string | null;
};

export type PositionReassessmentConfigUpdateBody = {
  enabled: boolean;
  cadence_minutes: number;
  notification_min_severity: ReassessmentSeverityWire;
};

export type TelegramAgentBotIdentityWire = {
  id: string | null;
  username: string | null;
  first_name: string | null;
};

export type TelegramAgentServiceStatusWire = {
  active: boolean;
  status: string;
  detail: string | null;
};

export type TelegramAgentTelemetryWire = {
  telegram_update_offset: number | null;
  event_cursor: number | null;
  last_update_received_at: string | null;
  last_notification_sent_at: string | null;
  last_notification_chat_id: string | null;
  last_error_at: string | null;
  last_error_type: string | null;
  last_error_message: string | null;
};

export type TelegramAgentRecentChatWire = {
  chat_id: string;
  user_id: string;
  username: string | null;
  first_name: string | null;
  authorized: boolean;
  last_seen_at: string;
};

export type TelegramAgentStatusWire = {
  enabled: boolean;
  authority_mode: TelegramAgentAuthorityMode;
  bot_token_present: boolean;
  agent_service_token_present: boolean;
  bot_api_reachable: boolean;
  bot_api_error: string | null;
  bot: TelegramAgentBotIdentityWire | null;
  configured_chat_ids: string[];
  configured_user_ids: string[];
  service: TelegramAgentServiceStatusWire;
  telemetry: TelegramAgentTelemetryWire;
  recent_chats: TelegramAgentRecentChatWire[];
};

export type TelegramAgentRecipientTestResultWire = {
  recipient_id: string;
  ok: boolean;
  detail: string;
};

export type TelegramAgentTestConnectionResponseWire = {
  status: "ok" | "partial" | "failed";
  bot_api_ok: boolean;
  bot: TelegramAgentBotIdentityWire | null;
  recipients: TelegramAgentRecipientTestResultWire[];
  error: string | null;
};

export type TelegramAgentTestNotificationResponseWire = {
  status: "queued_for_agent";
  event_type: string;
};

export type TelegramAgentRestartResponseWire = {
  status: "requested";
  service: string;
  detail: string | null;
};

// ---------------------------------------------------------------------
// Wizard + disclosures (Phase 4.5 D8)
// ---------------------------------------------------------------------

export type WizardEventRequest = {
  event_type: "disclosures_acknowledged" | "system_activated" | "news_inputs_activated";
  payload?: Record<string, unknown>;
};

export type WizardEventResponse = {
  accepted: true;
  event_type: string;
};

// Phase 5 D-Live wizard persistence.
export type WizardConfigureRequest = {
  risk_per_trade_pct: string; // Decimal-as-string in display-percent (e.g. "0.5")
  daily_loss_pct: string;
  weekly_loss_pct: string;
  max_open_positions: number;
  max_sector_pct: string;
  max_single_position_pct: string;
  sectors_followed: string[];
  universe_mode: "default" | "narrow";
  apply_lenses: boolean;
  ai_mode: "enabled" | "indicator_only";
  alloc_index: number;
  alloc_trading: number;
  alloc_investment: number;
  investment_setup: "skip" | "configure";
  investment_broker: "alpaca" | "fidelity" | "schwab" | "vanguard";
  pit_provider: "norgate" | "sharadar" | "none";
  news_provider: "polygon" | "benzinga" | "tiingo" | "none";
  enable_research_reports: boolean;
};

export type WizardConfigureResponse = {
  accepted: true;
  config_id: number;
};

// Phase 5 (d) follow-up — wizard credentials writer.
// All fields optional; the operator can update incrementally
// (e.g. rotate just Polygon without re-typing the rest). Empty
// strings are treated as "no change" by the backend, NOT as
// "clear me", to avoid accidental erasure.
export type WizardCredentialsRequest = {
  polygon_api_key?: string | null;
  alpaca_api_key?: string | null;
  alpaca_api_secret?: string | null;
  alpaca_live_api_key?: string | null;
  alpaca_live_api_secret?: string | null;
  anthropic_api_key?: string | null;
  openai_api_key?: string | null;
  google_ai_api_key?: string | null;
  news_api_key?: string | null;
  finnhub_api_key?: string | null;
  quiver_api_key?: string | null;
  edgar_contact_email?: string | null;
  coinbase_api_key?: string | null;
  coinbase_api_secret?: string | null;
  coinbase_live_api_key?: string | null;
  coinbase_live_api_secret?: string | null;
  glassnode_api_key?: string | null;
  coingecko_api_id?: string | null;
  coingecko_api_key?: string | null;
  coingecko_api_plan?: "demo" | "pro" | null;
  coindesk_api_key?: string | null;
  cointelegraph_api_key?: string | null;
  ntfy_topic?: string | null;
  ntfy_bearer_token?: string | null;
  telegram_bot_token?: string | null;
  // Phase 9 — tastyfx (IG US) forex broker. Demo env is the base
  // set; the _live set stores the production env for later. Saving
  // enables nothing: no forex adapter exists until Phase 9b, and
  // live forex additionally needs the Tier B override.
  tastyfx_username?: string | null;
  tastyfx_password?: string | null;
  tastyfx_account_id?: string | null;
  tastyfx_live_username?: string | null;
  tastyfx_live_password?: string | null;
  tastyfx_live_account_id?: string | null;
};

export type WizardCredentialsResponse = {
  // Names of the fields the backend wrote to the keychain.
  updated: string[];
  // True if the broker registry was rebuilt as part of the call.
  // The UI uses this to decide whether to re-fetch /accounts.
  broker_rebuilt: boolean;
};

// Live trading-account provisioning. Inserts an enabled trading_live
// row so saved live credentials become addressable. The row stays
// dark in the registry until the /before-live-trade ritual flips the
// three live-authorization gates (which this path NEVER touches).
export type ProvisionLiveAccountRequest = {
  broker_account_external_id: string;
  display_name: string;
  // Deliberately NOT widened to "forex" (unlike the read-side wire
  // types): the backend request model is equity|crypto and the
  // provisioning store hard-rejects anything but "equity" — no forex
  // broker exists to provision, live or otherwise (Phase 9b).
  asset_class?: "equity" | "crypto";
};

export type ProvisionLiveAccountResponse = {
  broker_account_id: string;
  broker: string;
  account_type: string;
  asset_class: string;
  broker_account_external_id: string;
  display_name: string;
  keychain_secret_id: string;
  enabled: boolean;
  // True when this call inserted the row; false when an enabled
  // trading_live row already existed (idempotent return).
  created: boolean;
  // The three live-authorization gates AFTER provisioning — proof
  // this path left them untouched.
  live_broker_readonly_enabled: boolean | null;
  live_trading_authorized_at: string | null;
  live_trading_authorization_ref: string | null;
};

// One-step "Test & provision live account" (UX §4.2). The probe
// fetches the account number from Alpaca; the operator supplies only a
// display name. No asset_class — live Alpaca is equity-only this phase.
export type TestAndProvisionLiveRequest = {
  display_name: string;
};

const testAndProvisionLiveResponseSchema = z.looseObject({
  provisioned: z.boolean(),
  created: z.boolean(),
  broker_account_id: z.string(),
  display_name: z.string(),
  account_number: z.string(),
  account_number_last4: z.string(),
  equity: z.string(),
  buying_power: z.string(),
  status: z.string(),
  live_broker_readonly_enabled: z.boolean().nullable(),
  authorized: z.boolean(),
});

export type TestAndProvisionLiveResponse = z.infer<
  typeof testAndProvisionLiveResponseSchema
>;

const pendingLiveAccountSchema = z.looseObject({
  broker_account_id: z.string(),
  broker: z.string(),
  display_name: z.string(),
  asset_class: z.string(),
  mask: z.string(),
  live_broker_readonly_enabled: z.boolean(),
  authorized: z.boolean(),
});

const pendingLiveAccountsResponseSchema = z.looseObject({
  accounts: z.array(pendingLiveAccountSchema),
});

export type PendingLiveAccount = z.infer<typeof pendingLiveAccountSchema>;
export type PendingLiveAccountsResponse = z.infer<
  typeof pendingLiveAccountsResponseSchema
>;

// Phase 5 D-Live read endpoints.
export type RiskLimitWire = {
  key:
    | "risk_per_trade"
    | "max_position"
    | "max_open_positions"
    | "daily_loss"
    | "weekly_loss"
    | "sector_concentration";
  label: string;
  value: string; // percent display or count
  unit: "%" | "count";
  quarterlyCap: boolean;
};

export type SectorLimitWire = {
  sector: string;
  capPct: string;
  usesDefault: boolean;
};

export type SectorLimitsWire = {
  broker_account_id: string;
  defaultCapPct: string;
  overrides: Record<string, string>;
  sectors: SectorLimitWire[];
  updatedAt?: string | null;
};

export type SectorLimitsUpdateBody = {
  broker_account_id: string;
  default_cap_pct: string | number;
  overrides: Record<string, string | number | null>;
};

export type PendingConfigChangeWire = {
  id: string;
  key: string;
  label: string;
  currentValue: string;
  newValue: string;
  unit: "%" | "count";
  submittedAtDisplay: string;
  activatesAtDisplay: string;
  activatesAtMs: number;
  reflection: string;
};

export type PendingApprovalWire = {
  approval_id: string;
  broker_account_id: string;
  // 2026-06-10 safety review (finding 1): the broker account's type
  // ("trading_paper" / "trading_live" / "investment") so the live-money
  // acknowledgment machinery can engage. null/absent → the UI falls
  // back to its conservative default mapping.
  account_type?: "trading_paper" | "trading_live" | "investment" | string | null;
  // Phase 9-UI: the broker account's asset_class ("equity" / "crypto"
  // / "forex"), resolved server-side. Lets the UI classify pending
  // rows directly instead of symbol-suffix sniffing (which cannot
  // work for forex symbols like "EUR/USD"). null/absent → fall back
  // to the legacy symbol heuristics.
  asset_class?: string | null;
  symbol: string;
  company_name?: string | null;
  side: "buy" | "sell";
  sized_shares: string;
  sized_entry_price: string;
  sized_stop_price: string | null;
  sized_target_prices: string[];
  // Added so the UI can compute dollar risk = (entry−stop) × qty
  // and risk-pct = dollar_risk / account_equity_snapshot.
  account_equity_snapshot: string;
  // Originating signal's timeframe (e.g. "swing", "1d") when recorded.
  timeframe?: string | null;
  signal_metadata?: Record<string, unknown>;
  agent_review?: MultiAgentReviewResultWire | null;
  requested_at: string;
  token_expires_at: string;
};

// Response shape for POST /api/v1/signals/generate.
// Mirrors SignalsGenerateResponseModel in _models.py.
export type SignalsGenerateWireResponse = {
  generated_at: string;
  as_of: string;
  summary: {
    universe_size: number;
    signals_generated: number;
    failures: number;
    regime: string;
    long_signals?: number;
    short_signals?: number;
    hedge_watch_signals?: number;
    auto_approval_confidence_threshold?: number;
  };
  signals: SignalWire[];
  failures: SignalFailureWire[];
  // approvals_created: UUIDs of auto-created Approval rows (8-hour TTL).
  approvals_created: string[];
  approvals_skipped: number;
};

export type SignalWire = {
  signal_id?: string | null;
  symbol: string;
  company_name?: string | null;
  strategy_id: string;
  asset_class?: "equity" | "crypto" | "forex";
  quantity_step?: string | null;
  min_quantity?: string | null;
  min_notional?: string | null;
  bar_date: string;
  direction: "long" | "short";
  entry_price: string;
  stop_price: string;
  target_prices: string[];
  score: number;
  factors: string[];
  rr_ratio: string;
  ai_confidence: number | null;
  ai_rationale: string | null;
  ai_risk_flags: string[];
  ai_model_id: string | null;
  research_enrichment?: Record<string, unknown> | null;
  edge_score?: number | null;
  regime_fit?: string | null;
  portfolio_fit?: string | null;
  approval_gate_reason?: string | null;
  signal_role?: "entry" | "hedge_watch";
};

export type SignalFailureWire = {
  symbol: string;
  error_type:
    | "insufficient_history"
    | "indicator_nan"
    | "strategy_runtime"
    | "database_error"
    | "scorer_runtime";
  detail: string;
};

export type SignalHistoryStatusWire =
  | "skipped"
  | "pending"
  | "expired"
  | "approved"
  | "placed";

export type SignalHistoryWire = {
  signal_id: string;
  generated_at: string;
  symbol: string;
  company_name?: string | null;
  strategy_id: string;
  asset_class?: "equity" | "crypto" | "forex";
  bar_date: string;
  direction: "long" | "short";
  timeframe: string;
  entry_price: string;
  stop_price: string;
  target_prices: string[];
  rr_ratio: string;
  score: number;
  rationale: string;
  ai_model_id: string | null;
  ai_input_mode: string | null;
  research_enrichment?: Record<string, unknown> | null;
  edge_score?: number | null;
  regime_fit?: string | null;
  portfolio_fit?: string | null;
  approval_gate_reason?: string | null;
  signal_role?: "entry" | "hedge_watch";
  approval_status: SignalHistoryStatusWire;
  approval_id: string | null;
  approval_requested_at: string | null;
  approval_expires_at: string | null;
  approval_consumed_at: string | null;
};

export type SignalsHistoryResponseWire = {
  signals: SignalHistoryWire[];
};

export type ResearchReportWire = {
  research_report_id: string;
  symbol: string;
  requested_by: string;
  requested_at: string;
  completed_at: string | null;
  model_version: string;
  prompt_version: string;
  asset_class: string;
  primary_documents: Record<string, unknown>[];
  news_headlines_used: Record<string, unknown>[] | null;
  fundamentals_used: Record<string, unknown> | null;
  report_kind?: string;
  section_labels?: Record<string, string>;
  suggested_signal?: ResearchSuggestedSignalWire | null;
  report_text: string;
  report_sections: Record<string, string>;
  bull_case_length: number;
  bear_case_length: number;
  citation_count: number;
  primary_doc_quote_count: number;
  validator_passed: boolean;
  validator_failures: string[] | null;
  cost_dollars: string;
  cached_until: string;
  cached_expired: boolean;
  superseded_by: string | null;
};

export type ResearchReportArchiveResponseWire = {
  reports: ResearchReportWire[];
};

export type ResearchSuggestedSignalWire = {
  mode: "display_only";
  stance: "long" | "short" | "flat";
  entry_price: string | null;
  stop_price: string | null;
  target_prices: string[];
  risk_reward_ratio: string | null;
  strategy_id: string | null;
  confidence: string | null;
  invalidation_text: string;
  thesis_summary: string;
  llm_adjustment_note: string;
  validation_status: string;
  source: string;
  evaluated_at: string | null;
  expires_at: string | null;
};

export type ResearchReportCreateResponseWire = {
  research_report_id: string;
  symbol: string;
  report_kind?: string;
  validator_passed: boolean;
  cache_hit: boolean;
  cost_dollars: string;
  completed_at: string | null;
  cached_until: string;
  suggested_signal?: ResearchSuggestedSignalWire | null;
  validator_failures?: string[];
};

export type AccountWire = {
  broker_account_id: string;
  label: string;
  broker: string;
  mask: string;
  equity: string;
  buying_power: string;
  cash: string;
  today_pnl: string;
  pattern_day_trader: boolean;
  trading_blocked: boolean;
  asset_class?: "equity" | "crypto" | "forex";
  account_type?: "trading_paper" | "trading_live" | "investment" | string;
  broker_equity?: string | null;
  balance_source?:
    | "broker"
    | "asset_class_sleeve"
    | "asset_class_exposure"
    | "shared_broker_residual"
    | string;
  sleeve_allocation?: string | null;
  sleeve_equity?: string | null;
  sleeve_cash?: string | null;
  sleeve_realized_pnl?: string | null;
  sleeve_unrealized_pnl?: string | null;
};

export type PositionWire = {
  broker_account_id: string;
  symbol: string;
  company_name?: string | null;
  qty: number | string;
  side: "buy" | "sell";
  avg_entry_price: string;
  market_value: string;
  stop_price?: string | null;
  stop_status?: string | null;
  target_price?: string | null;
  target_status?: string | null;
  // Phase 9-UI: resolved server-side from the position's broker
  // account. "equity" default preserves the legacy assumption.
  asset_class?: string;
};

export type ClosedStockTradeWire = {
  fillId: string;
  approvalId: string;
  brokerAccountId: string;
  accountLabel: string;
  symbol: string;
  companyName?: string | null;
  side: "long" | "short";
  qty: number | string;
  entryTime: string;
  exitTime: string;
  entryPrice: string;
  exitPrice: string;
  realizedPnl: string;
  rMultiple: string | null;
  exitLegType:
    | "stop"
    | "target"
    | "exit"
    | "adjustment"
    | "target_fallback"
    | "stop_fallback"
    | "reassessment_exit";
  strategyId: string | null;
};

export type ClosedStockTradesResponseWire = {
  asOf: string;
  rows: ClosedStockTradeWire[];
};

// ----- Phase 7e / 7d / 7c read-list wire types -----

export type PositionTradeWire = {
  position_trade_id: string;
  symbol: string;
  strategy_id: string;
  entry_thesis: string;
  invalidation_conditions: string;
  expected_holding_months: number;
  current_stop_price: string;
  opened_at: string;
  last_review_at: string | null;
  review_skipped_count: number;
  held_days: number;
  reviewed_days_ago: number;
  review_overdue_days: number | null;
};

export type InvestmentPositionWire = {
  investment_position_id: string;
  symbol: string;
  target_allocation_pct: string;
  opened_at: string;
  status: string;
  next_review_due_at: string;
  review_status: "ok" | "due" | "overdue";
  research_report_id: string | null;
  consecutive_reviews_skipped: number;
  held_months: number;
};

export type BasketWire = {
  basket_id: string;
  name: string;
  description: string;
  members: string[];
  max_concentration_pct: string;
  for_account: string;
  exposure: number;
  signals24h: number;
};

// ----- Phase 5 (d) Open-Position Management (D1/D4) wire types -----

export type TightenStopRequestBody = {
  new_stop_price: string;
  preset?:
    | "break_even"
    | "plus_0_5r"
    | "plus_1r"
    | "trailing_2_atr"
    | "custom";
  idempotency_key: string;
};

export type TightenStopResponseWire = {
  broker_account_id: string;
  symbol: string;
  old_stop_price: string;
  new_stop_price: string;
  broker_order_id: string | null;
  modified_at: string;
};

export type AdjustTargetRequestBody = {
  new_target_price: string;
  idempotency_key: string;
};

export type AdjustTargetResponseWire = {
  broker_account_id: string;
  symbol: string;
  old_target_price: string;
  new_target_price: string;
  loosened: boolean;
  broker_order_id: string | null;
  modified_at: string;
};

export type PositionActionIdempotentRequestBody = {
  idempotency_key: string;
};

export type ExitAtLimitRequestBody = {
  limit_price: string;
  idempotency_key: string;
};

export type PositionAdjustQtyRequestBody = {
  target_risk_dollars: string;
  idempotency_key: string;
};

export type ExitAtMarketResponseWire = {
  broker_account_id: string;
  symbol: string;
  qty: number | string;
  broker_order_id: string | null;
  submitted_at: string;
};

export type ExitAtLimitResponseWire = {
  broker_account_id: string;
  symbol: string;
  qty: number | string;
  limit_price: number | string;
  broker_order_id: string | null;
  submitted_at: string;
};

export type PositionAdjustQtyResponseWire = {
  broker_account_id: string;
  symbol: string;
  old_qty: string;
  new_qty: string;
  delta_qty: string;
  action: "add" | "trim";
  broker_order_id: string | null;
  preview: TradePreviewWire;
  exit_resize_warning: string | null;
  submitted_at: string;
};

export type LivePnLEntryWire = {
  broker_account_id: string;
  symbol: string;
  qty: number | string;
  avg_entry_price: string;
  current_price: string | null;
  unrealized_pnl: string;
  unrealized_pnl_pct: string;
  stale: boolean;
};

export type LivePnLResponseWire = {
  as_of: string;
  market_open: boolean;
  entries: LivePnLEntryWire[];
};

export type PositionReassessmentWire = {
  reassessmentId: string;
  snapshotId: string | null;
  brokerAccountId: string;
  symbol: string;
  companyName?: string | null;
  assetClass: "equity" | "crypto" | "forex";
  side: "long" | "short";
  recommendation: "hold" | "tighten_stop" | "trim" | "exit" | "manual_review";
  proposedAction: "none" | "tighten_stop" | "trim" | "exit_market";
  severity: "low" | "medium" | "high";
  confidence: "low" | "medium" | "high";
  status: "active" | "dismissed" | "confirmed" | "expired";
  fingerprint: string;
  currentSnapshot: Record<string, unknown>;
  changedConditions: Record<string, unknown>[];
  deterministicResult: Record<string, unknown>;
  llmDebate: Record<string, unknown>;
  blockers: Record<string, unknown>[];
  actionPayload: Record<string, unknown>;
  statusNote: string | null;
  expiresAt: string;
  createdAt: string;
  dismissedAt: string | null;
  confirmedAt: string | null;
  actionResult: Record<string, unknown>;
};

export type PositionReassessmentListResponseWire = {
  asOf: string;
  rows: PositionReassessmentWire[];
};

export type PositionReassessmentRunResponseWire =
  PositionReassessmentListResponseWire & {
    totalPositions: number;
    reviewed: number;
    persisted: number;
    skipped: number;
    degraded: number;
  };

// ----- Phase 5 a.7 — Health-page backup panel wire types -----

export type BackupTestOutcomeWire = {
  occurred_at: string; // ISO-8601
  event_type: "restore_test_passed" | "restore_test_failed";
  backup_path: string | null;
  duration_seconds: number | null;
  failed_criteria: string[] | null;
};

export type BackupTestsResponseWire = {
  outcomes: BackupTestOutcomeWire[];
};

export type BackupVerifyCheckWire = {
  name: string;
  status: "passed" | "failed";
  detail: string;
};

export type BackupVerifyResponseWire = {
  status: "passed" | "failed" | "no_backup";
  backup_path: string | null;
  chain_head_hex: string | null;
  checks: BackupVerifyCheckWire[];
};

export type DisclosuresStatusResponse = {
  needs_reprompt: boolean;
  reason: "current" | "age" | "hash_mismatch" | "never_confirmed";
  last_acknowledged_at: string | null;
};

export type SystemActivationStatusResponse = {
  activated: boolean;
  activated_at: string | null;
};

// Phase 5 (d) — recent-events feed wire types.
export type EventRowWire = {
  event_id?: number | null;
  occurred_at?: string | null;
  time: string; // "HH:MM:SS" UTC
  type: string; // raw event_type from audit log
  summary: string;
  actor: "user" | "system" | "broker";
};

export type RecentEventsResponseWire = {
  events: EventRowWire[];
};

// Phase 7a D8 — monthly AI cost status wire type.
export type AiCostStatusWire = {
  monthly_calls: number;
  estimated_cost_usd: string;
  ceiling_usd: string;
  near_ceiling: boolean;
  news_provider_name: string;
  quality_tier_warning: boolean;
};

// ----- Phase 8f — crypto paper-validation gate -----

export type CryptoPaperValidationDayRequest = {
  validation_date: string;
  reviewed_by: string;
  manual_approval_required: boolean;
  crypto_signals: number;
  approvals_created: number;
  approvals_confirmed: number;
  broker_orders_placed: number;
  auto_approved_count: number;
  guardrail_breaches: number;
  realized_pnl: string;
  notes: string;
};

export type CryptoPaperValidationDayResponse = {
  validation_date: string;
};

export type CryptoPaperValidationGateResponse = {
  passed: boolean;
  days_required: number;
  days_reviewed: number;
  total_realized_pnl: string;
  failures: string[];
};

export type CryptoValidationRunWire = {
  validation_run_id: string;
  strategy_id: string;
  created_at: string;
  period_start: string;
  period_end: string;
  status: "passed" | "failed";
  passed: boolean;
  summary: Record<string, unknown>;
  artifacts: Record<string, unknown>;
};

export type CryptoValidationRunListResponseWire = {
  runs: CryptoValidationRunWire[];
};

// ----- Phase 8 — live crypto watchlist analysis -----

export type CryptoWatchlistAnalysisRowWire = {
  symbol: string;
  venue: "Alpaca";
  instrument_type: "spot";
  last_price: string | null;
  trigger_price: string | null;
  distance_pct: string | null;
  proximity: number;
  plan: string;
  blocked_reason: string | null;
  depth_state: "ok" | "watch" | "unknown";
  funding_rate: string | null;
  regime_label: string | null;
  ai_confidence: number | null;
  det_score: number | null;
  analysis: string;
  cited: string;
  as_of: string;
};

export type CryptoWatchlistAnalysisResponseWire = {
  generated_at: string;
  universe: string;
  rows: CryptoWatchlistAnalysisRowWire[];
};

export type MoonshotProviderStatusWire = {
  source: string;
  status: "ready" | "degraded" | "unavailable";
  detail: string;
};

export type MoonshotCatalystWire = {
  date: string;
  label: string;
  note: string;
  source: string;
};

export type MoonshotNewsItemWire = {
  date: string;
  headline: string;
  source: string;
};

export type MoonshotPublicDisclosureWire = {
  count: number;
  note: string;
  status: "ready" | "degraded" | "unavailable";
};

export type MoonshotStockSignalWire = {
  ticker: string;
  company_name: string;
  sector: string;
  risk_tier: "Speculative" | "High Growth";
  rank: number;
  research_priority_score: number;
  rank_reasons: string[];
  data_quality: "complete" | "partial" | "limited";
  current_price: string;
  percent_change: string;
  unusual_volume_ratio: string;
  next_earnings_date: string | null;
  catalysts: MoonshotCatalystWire[];
  recent_news: MoonshotNewsItemWire[];
  public_disclosures: MoonshotPublicDisclosureWire;
  risk_read: string;
  sample_as_of: string | null;
  data_as_of: string;
  data_delay: string;
};

export type MoonshotSignalsResponseWire = {
  generated_at: string;
  universe: string;
  sample: boolean;
  provider_statuses: MoonshotProviderStatusWire[];
  rows: MoonshotStockSignalWire[];
};

export type MoonshotSignalsRequest = {
  asOf?: string;
  universe?: string;
  limit?: number;
  maxPrice?: number | string;
  forceRefresh?: boolean;
};

export type MoonshotAIRankRowWire = {
  ticker: string;
  deterministic_rank: number;
  ai_rank: number;
  ai_score: number;
  ai_reason: string;
  confidence: "low" | "medium" | "high";
  risk_flags: string[];
};

export type MoonshotAIRankResponseWire = {
  generated_at: string;
  universe: string;
  status: "ready" | "cached" | "degraded";
  detail: string;
  model_id: string | null;
  source_rank_limit: number;
  rows: MoonshotAIRankRowWire[];
};

export type CryptoPaperEngineStatusWire = {
  auto_trading_enabled: boolean;
  auto_approval_enabled: boolean;
  auto_trading_disabled?: boolean;
  auto_trading_disabled_reason?: string | null;
  registered_crypto_paper_adapters: number;
  max_open_trades: number;
  entries_today?: number;
  max_daily_entries: number;
  risk_per_trade_pct: string;
  exit_mode?: string;
  exit_intents_by_status: Record<string, number>;
  last_run?: CryptoPaperEngineRunResponseWire & {
    requested_at?: string;
    started_at?: string;
    completed_at?: string;
  };
};

export type CryptoPaperTradingConfigWire = {
  auto_trading_enabled: boolean;
  auto_approval_enabled: boolean;
  max_open_trades: number;
  max_daily_entries: number;
  risk_per_trade_pct: string;
  updated_at: string | null;
  updated_by: string | null;
};

export type CryptoPaperTradingConfigUpdateWire = {
  auto_trading_enabled: boolean;
  auto_approval_enabled: boolean;
  max_open_trades: number;
  max_daily_entries: number;
  risk_per_trade_pct: string;
};

// Phase 9-UI: read-only forex gate view (GET /api/v1/config/forex).
// Mirrors ForexConfigResponseModel. There is deliberately no write
// wire type — the gate flips via manual operator UPDATE only
// (Override 5 Tier A).
export type ForexConfigWire = {
  forex_trading_enabled: boolean;
  forex_trading_enabled_at?: string | null;
  forex_trading_enabled_by?: string | null;
};

// One fx_pairs reference row (GET /api/v1/forex/pairs). Mirrors
// ForexPairModel — numerics arrive Decimal-as-string per the
// platform wire convention.
export type ForexPairWire = {
  symbol: string;
  base_currency: string;
  quote_currency: string;
  pip_size: string;
  price_decimal_places: number;
  min_unit_size: string;
  margin_requirement_pct: string;
  is_active: boolean;
};

export type ForexPairsResponseWire = {
  pairs: ForexPairWire[];
};

// Registration state of the tastyfx demo broker-account row
// (GET/POST /api/v1/forex/account). Mirrors ForexAccountResponseModel.
// registered=false → no row exists; identity fields are then null.
// The row is always DISABLED (enabled=false) until the Phase 9b
// adapter + its activation migration land — the UI renders it as
// "registered, dormant". The account identifier arrives MASKED to
// its last 4 characters — the tastyfx account ID is also a keychain
// credential, so the full value never reaches frontend state.
export type ForexAccountWire = {
  registered: boolean;
  created?: boolean;
  broker_account_id?: string | null;
  broker?: string | null;
  account_type?: string | null;
  asset_class?: string | null;
  external_id_last4?: string | null;
  display_name?: string | null;
  enabled?: boolean | null;
};

export type ForexAccountRegisterRequest = {
  // The tastyfx DEMO account identifier value.
  broker_account_external_id: string;
  display_name: string;
};

export type CryptoExchangeCapabilityWire = {
  exchange_id: string;
  display_name: string;
  spot_supported_by_freqtrade: boolean;
  tradepro_stage:
    | "live_enabled"
    | "paper_enabled"
    | "next_candidate"
    | "research_only"
    | "backlog";
  live_trading_enabled: boolean;
  paper_trading_enabled: boolean;
  data_adapter: string | null;
  broker_adapter: string | null;
  stoploss_on_exchange: string | null;
  requires_passphrase: boolean;
  notes: string[];
};

export type CryptoExchangeCapabilityListResponseWire = {
  exchanges: CryptoExchangeCapabilityWire[];
  live_enabled_exchange_ids: string[];
  recommended_live_rollout: string[];
};

export type CryptoPaperTradeHistoryRowWire = {
  intent_id: string;
  approval_id: string;
  broker_account_id: string;
  symbol: string;
  strategy_id: string;
  entry_at: string;
  closed_at: string;
  entry_price: string;
  exit_price: string;
  qty: string;
  realized_pnl: string;
  outcome_r: string;
  exit_reason: string;
  score: string | null;
  ai_confidence: string | null;
  det_score: string | null;
  proximity: number | null;
};

export type CryptoPaperTradeHistoryResponseWire = {
  rows: CryptoPaperTradeHistoryRowWire[];
};

export type CryptoSignalFilterWire = {
  rule: string;
  passed?: boolean | null;
  allowed?: boolean | null;
  reason: string;
  score_adjustment?: number | null;
  lock_symbol?: string | null;
};

export type CryptoSignalCandidateWire = {
  symbol: string;
  strategy_id: string;
  side: "buy";
  entry: string | null;
  stop: string | null;
  target: string | null;
  det_score: number;
  ai_confidence: number | null;
  final_score: number;
  rank: number;
  approval_id: string | null;
  approval_status: "queued" | "not_queued";
  gate_status: "queued" | "blocked" | "watch_only";
  gate_reason: string;
  pair_filters: CryptoSignalFilterWire[];
  protections: CryptoSignalFilterWire[];
  rationale: string;
  features: Record<string, unknown>;
  learning_adjustment: number;
  as_of: string;
};

export type CryptoSignalsGenerateResponseWire = {
  run_id: string;
  status: "ok" | "skipped";
  generated_at: string;
  universe: string;
  mode: "queue_approvals";
  summary: {
    candidates_generated: number;
    candidates_scored: number;
    approvals_queued: number;
    blocked: number;
    watch_only: number;
    auto_approval_confidence_threshold: number;
  };
  candidates: CryptoSignalCandidateWire[];
};

export type CryptoPaperEngineRunResponseWire = {
  status: string;
  reason?: string | null;
  candidates_scored: number;
  approvals_created: number;
  orders_placed: number;
  failures?: { symbol: string; error: string }[];
};

// ----- Charts workspace -----

export type ChartSymbolWire = {
  symbol: string;
};

export type ChartBarWire = {
  time: string;
  open: string;
  high: string;
  low: string;
  close: string;
  volume: number;
};

export type ChartLinePointWire = {
  time: string;
  value: string;
};

export type ChartLevelWire = {
  id: string;
  kind: "entry" | "stop" | "target";
  price: string;
  label: string;
  status: string;
};

export type ChartMarkerWire = {
  id: string;
  kind: "signal" | "entry" | "exit";
  time: string;
  price: string;
  text: string;
  position: "aboveBar" | "belowBar";
  color: string;
  shape: "arrowUp" | "arrowDown" | "circle" | "square";
};

export type ChartTradeSpanWire = {
  approvalId: string;
  entryTime: string;
  exitTime: string;
  entryPrice: string;
  exitPrice: string;
  qty: number | string;
  pnl: string;
  rMultiple: string | null;
};

export type ChartTimeframeWire = "1D" | "1m" | "5m" | "15m" | "1h";

export type ChartSnapshotWire = {
  symbol: string;
  timeframe: ChartTimeframeWire;
  bars: ChartBarWire[];
  overlays: {
    sma20: ChartLinePointWire[];
    sma50: ChartLinePointWire[];
    sma200: ChartLinePointWire[];
    rsi14: ChartLinePointWire[];
  };
  levels: ChartLevelWire[];
  markers: ChartMarkerWire[];
  trades: ChartTradeSpanWire[];
};

// ---------------------------------------------------------------------
// Research Workbench wire types
// ---------------------------------------------------------------------

export type StrategyVoteWire = {
  strategy_id: string;
  vote: "bullish" | "bearish" | "neutral";
  score: number;
  factors: string[];
  direction: "long" | "short" | null;
  entry_price: string | null;
  stop_price: string | null;
  target_prices: string[];
  reason: string;
};

export type EvaluationResultWire = {
  evaluation_id: string;
  symbol: string;
  is_etf: boolean;
  horizon: string;
  as_of_date: string;
  investment_stance: "buy" | "hold" | "avoid";
  trade_stance: "long" | "short" | "flat";
  composite_score: number;
  confidence: "high" | "medium" | "low";
  regime: string;
  strategy_votes: StrategyVoteWire[];
  entry_price: string | null;
  stop_price: string | null;
  target_prices: string[];
  invalidation_text: string;
  news_count: number;
  has_fundamentals: boolean;
  has_macro: boolean;
  portfolio_already_held: boolean;
  live_price?: string | null;
  live_price_source?: string;
  latest_bar_date?: string | null;
  latest_bar_close?: string | null;
  latest_news_at?: string | null;
  score_drivers?: string[];
  data_freshness?: Record<string, unknown>;
  analyst_summary?: string;
  bull_case?: string[];
  bear_case?: string[];
  key_risks?: string[];
  fundamentals_summary?: string[];
  macro_summary?: string[];
  news_digest?: Array<Record<string, unknown>>;
  valuation_summary?: string[];
  sector_peer_summary?: string[];
  news_catalysts?: string[];
  source_notes?: Array<Record<string, unknown>>;
  score_attribution?: Record<string, unknown>;
  peer_context?: Record<string, unknown>;
  sector_context?: Record<string, unknown>;
  filing_context?: Record<string, unknown>;
  ai_memo_status?: "ready" | "unavailable" | "failed";
  ai_memo_model?: string | null;
  ai_memo_cost?: string | null;
  ai_memo_error?: string | null;
  trade_plan?: Record<string, unknown>;
  chart_levels: ChartLevelWire[];
  chart_markers: ChartMarkerWire[];
  social_sentiment?: SocialSentimentSnapshotWire | null;
  agent_review?: MultiAgentReviewResultWire | null;
  evaluated_at: string;
};

export type SocialSentimentPostWire = {
  post_id: string;
  source: string;
  author: string | null;
  published_at: string | null;
  sentiment: "bullish" | "bearish" | "neutral" | "unknown";
  engagement: Record<string, number>;
  body: string;
  url: string | null;
};

export type SocialSentimentSnapshotWire = {
  snapshot_id: string;
  symbol: string;
  as_of: string;
  provider_status: Record<string, string>;
  filtered_count: number;
  post_count: number;
  counts: Record<string, number>;
  posts: SocialSentimentPostWire[];
};

export type AgentStepResultWire = {
  role: string;
  stance: "supportive" | "skeptical" | "neutral";
  summary: string;
  key_points: string[];
  concerns: string[];
  source_ids: string[];
};

export type MultiAgentReviewResultWire = {
  review_id: string;
  evaluation_id: string;
  workflow_run_id: string | null;
  symbol: string;
  as_of_date: string;
  status: "ready" | "failed";
  synthesis_summary: string;
  key_opportunities: string[];
  key_risks: string[];
  citations: string[];
  agent_steps: AgentStepResultWire[];
  source_composite_score: number;
  prompt_version: string;
  model_id: string;
  created_at: string;
  metadata: Record<string, unknown>;
};

export type RunAgentReviewResponseWire = {
  review: MultiAgentReviewResultWire;
  workflow: Record<string, unknown> | null;
  social_sentiment: SocialSentimentSnapshotWire | null;
};

export type ShadowAgentReviewRunResponseWire = {
  workflow: Record<string, unknown> | null;
  reviewed: number;
  failed: number;
  review_ids: string[];
  failures: Array<Record<string, string>>;
};

export type DecisionReflectionWire = {
  reflection_id: string;
  source_decision_type: string;
  source_decision_id: string;
  symbol: string;
  decision_at: string;
  evaluated_at: string;
  holding_window_days: number;
  raw_return_pct: string;
  benchmark_return_pct: string | null;
  relative_return_pct: string | null;
  lesson: string;
  confidence: "low" | "medium" | "high";
  prompt_version: string;
  model_id: string;
  metadata: Record<string, unknown>;
};

export type DecisionReflectionsResponseWire = {
  reflections: DecisionReflectionWire[];
};

export type AIWorkflowStatusResponseWire = {
  run: Record<string, unknown>;
  steps: Array<Record<string, unknown>>;
};

export type AIProviderCapabilitiesWire = {
  structured_output: boolean;
  tool_choice: boolean;
  reasoning_effort: boolean;
  timeout_seconds: number;
  local_model: boolean;
  caveats: string[];
};

export type AIProviderConfigWire = {
  config_id: string | null;
  feature: string;
  provider: string;
  model_id: string;
  base_url: string | null;
  api_key_service: string | null;
  api_key_account: string;
  timeout_seconds: number;
  enabled: boolean;
  capabilities: AIProviderCapabilitiesWire | null;
  created_at: string | null;
  updated_at: string | null;
};

export type AIProviderConfigListResponseWire = {
  configs: AIProviderConfigWire[];
};

export type AIProviderPreflightResponseWire = {
  features: Record<string, unknown>;
};

export type AIBreakerResetResponseWire = {
  feature: string;
  breaker: Record<string, unknown>;
};

export type SavedResearchEvaluationWire = EvaluationResultWire & {
  accepted_at: string;
  approval_id: string | null;
};

export type SavedResearchEvaluationsResponseWire = {
  evaluations: SavedResearchEvaluationWire[];
};

export type AcceptEvaluationResponseWire = {
  evaluation_id: string;
  accepted_at: string;
};

export type CreateApprovalFromEvalRequestBody = {
  evaluation_id: string;
  broker_account_id: string;
  sized_shares: string;
  account_equity_snapshot: string;
};

export type CreateApprovalFromEvalResponseWire = {
  evaluation_id: string;
  approval_id: string;
  symbol: string;
  side: "buy" | "sell";
  sized_shares: string;
  sized_entry_price: string;
  sized_stop_price: string | null;
  sized_target_prices: string[];
  requested_at: string;
  token_expires_at: string;
};

// ---------------------------------------------------------------------
// Login + sessions (Phase 5 D-A)
// ---------------------------------------------------------------------

export type LoginRequestBody = {
  username: string;
  password: string;
};

export type LoginResponse = {
  user_id: string;
  username: string;
};

export type MeResponse = {
  user_id: string;
  username: string;
};

export type SetupStatusResponse = {
  setup_required: boolean;
  admin_exists: boolean;
  token_required: boolean;
  token_valid: boolean | null;
};

export type SetupAdminRequest = {
  setup_token: string;
  username: string;
  password: string;
  client_type?: "browser" | "mobile";
};

export type SetupAdminResponse = {
  user_id: string;
  username: string;
  session_token: string | null;
};

// ---------------------------------------------------------------------
// API client
// ---------------------------------------------------------------------

export type ApiClientConfig = {
  baseUrl?: string;
  fetch?: typeof fetch;
  // Phase 5 D-A: 401 handler. When the app process responds 401
  // on a protected endpoint (stale cookie, revoked session,
  // expired session) the client invokes this hook so the UI can
  // route back to the login screen. Optional; if omitted, the
  // ProblemDetailError flows up unchanged.
  onUnauthorized?: () => void;
};

export class ApiClient {
  private readonly baseUrl: string;
  private readonly fetchImpl: typeof fetch;
  private readonly onUnauthorized?: () => void;

  constructor(config: ApiClientConfig = {}) {
    this.baseUrl = config.baseUrl ?? DEFAULT_BASE_URL;
    // CRITICAL: window.fetch must be invoked with window/globalThis
    // as `this` — assigning the bare function reference to a class
    // property strips that binding and the runtime throws
    // "TypeError: Failed to execute 'fetch' on 'Window': Illegal
    // invocation" on the next call. Bind explicitly here so the
    // production path always has a usable fetch. Test stubs
    // (vi.fn()) don't care about `this` so the bug only surfaces
    // against real browser fetch.
    this.fetchImpl = config.fetch ?? fetch.bind(globalThis);
    this.onUnauthorized = config.onUnauthorized;
  }

  // ----- /api/v1/login + /logout + /me (Phase 5 D-A) -----

  async login(body: LoginRequestBody): Promise<LoginResponse> {
    return this.call<LoginResponse>("POST", "/api/v1/login", body);
  }

  async logout(): Promise<{ status: string }> {
    return this.call<{ status: string }>("POST", "/api/v1/logout");
  }

  async me(): Promise<MeResponse> {
    return this.call<MeResponse>("GET", "/api/v1/me");
  }

  // ----- /api/v1/setup/* (fresh-instance onboarding) -----

  async getSetupStatus(
    setupToken?: string | null,
  ): Promise<SetupStatusResponse> {
    const qs = setupToken
      ? `?${new URLSearchParams({ setup_token: setupToken }).toString()}`
      : "";
    return this.call<SetupStatusResponse>(
      "GET",
      `/api/v1/setup/status${qs}`,
    );
  }

  async createSetupAdmin(
    body: SetupAdminRequest,
  ): Promise<SetupAdminResponse> {
    return this.call<SetupAdminResponse>(
      "POST",
      "/api/v1/setup/admin",
      body,
    );
  }

  // ----- /health -----

  async getHealth(): Promise<HealthResponse> {
    // /health returns 503 when aggregate status is "down" (load-balancer
    // semantics per TDD §14.2), but the response body is always a valid
    // HealthResponse. Accept both 200 and 503 so the Subsystems panel
    // can display subsystem details even when overall health is "down".
    return this.call<HealthResponse>("GET", "/health", undefined, {
      acceptStatuses: [503],
    });
  }

  // ----- /api/v1/approvals/* -----

  async requestApproval(
    body: ApprovalRequestBody,
  ): Promise<ApprovalResponse> {
    return this.call<ApprovalResponse>(
      "POST",
      "/api/v1/approvals/request",
      body,
    );
  }

  async confirmApproval(
    body: ConfirmRequestBody,
  ): Promise<ConfirmResponse> {
    return this.call<ConfirmResponse>(
      "POST",
      "/api/v1/approvals/confirm",
      body,
    );
  }

  async confirmAndPlaceApproval(
    body: ConfirmRequestBody,
  ): Promise<ConfirmAndPlaceResponse> {
    const path = "/api/v1/approvals/confirm-and-place";
    const raw = await this.call<unknown>("POST", path, body);
    return validateWire<ConfirmAndPlaceResponse>(
      path,
      confirmAndPlaceResponseSchema,
      raw,
    );
  }

  async reviewApproval(
    approvalId: string,
  ): Promise<ApprovalReviewResponse> {
    const path = `/api/v1/approvals/review/${approvalId}`;
    const raw = await this.call<unknown>("GET", path);
    return validateWire<ApprovalReviewResponse>(
      path,
      approvalReviewResponseSchema,
      raw,
    );
  }

  async previewApproval(
    body: ApprovalRepriceRequestBody,
  ): Promise<ApprovalPreviewResponse> {
    return this.call<ApprovalPreviewResponse>(
      "POST",
      "/api/v1/approvals/preview",
      body,
    );
  }

  async repriceApproval(
    body: ApprovalRepriceRequestBody,
  ): Promise<ApprovalRepriceResponse> {
    return this.call<ApprovalRepriceResponse>(
      "POST",
      "/api/v1/approvals/reprice",
      body,
    );
  }

  async resizeApproval(
    approvalId: string,
    riskDollars: string,
  ): Promise<ApprovalResizeResponse> {
    return this.call<ApprovalResizeResponse>(
      "POST",
      `/api/v1/approvals/${encodeURIComponent(approvalId)}/resize`,
      { risk_dollars: riskDollars },
    );
  }

  // ----- /api/v1/risk/snapshot -----

  async getRiskSnapshot(
    brokerAccountId: string,
  ): Promise<RiskSnapshotResponse> {
    const params = new URLSearchParams({
      broker_account_id: brokerAccountId,
    });
    return this.call<RiskSnapshotResponse>(
      "GET",
      `/api/v1/risk/snapshot?${params.toString()}`,
    );
  }

  // ----- /api/v1/config/change -----

  async changeConfig(
    body: ConfigChangeRequestBody,
  ): Promise<ConfigChangeResponse> {
    return this.call<ConfigChangeResponse>(
      "POST",
      "/api/v1/config/change",
      body,
    );
  }

  async getCoolingOffConfig(): Promise<CoolingOffConfigWire> {
    return this.call<CoolingOffConfigWire>(
      "GET",
      "/api/v1/config/cooling-off",
    );
  }

  async setCoolingOffConfig(
    body: CoolingOffConfigUpdateBody,
  ): Promise<CoolingOffConfigWire> {
    return this.call<CoolingOffConfigWire>(
      "PUT",
      "/api/v1/config/cooling-off",
      body,
    );
  }

  async getAutoApprovalsConfig(): Promise<AutoApprovalsConfigWire> {
    return this.call<AutoApprovalsConfigWire>(
      "GET",
      "/api/v1/config/auto-approvals",
    );
  }

  async setAutoApprovalsConfig(
    body: AutoApprovalsConfigUpdateBody,
  ): Promise<AutoApprovalsConfigWire> {
    return this.call<AutoApprovalsConfigWire>(
      "PUT",
      "/api/v1/config/auto-approvals",
      body,
    );
  }

  async listStrategyRoutes(): Promise<StrategyRouteWire[]> {
    const path = "/api/v1/strategies/routes";
    const raw = await this.call<unknown>("GET", path);
    return validateWire<StrategyRouteWire[]>(
      path,
      strategyRoutesWireSchema,
      raw,
    );
  }

  async updateStrategyRoute(
    strategyId: string,
    body: StrategyRouteUpdateBody,
  ): Promise<StrategyRouteWire> {
    const path = `/api/v1/strategies/${encodeURIComponent(strategyId)}/route`;
    const raw = await this.call<unknown>("PATCH", path, body);
    return validateWire<StrategyRouteWire>(
      path,
      strategyRouteWireSchema,
      raw,
    );
  }

  async getTelegramAgentConfig(): Promise<TelegramAgentConfigWire> {
    return this.call<TelegramAgentConfigWire>(
      "GET",
      "/api/v1/config/telegram-agent",
    );
  }

  async getTelegramAgentStatus(): Promise<TelegramAgentStatusWire> {
    return this.call<TelegramAgentStatusWire>(
      "GET",
      "/api/v1/config/telegram-agent/status",
    );
  }

  async setTelegramAgentConfig(
    body: TelegramAgentConfigUpdateBody,
  ): Promise<TelegramAgentConfigWire> {
    return this.call<TelegramAgentConfigWire>(
      "PUT",
      "/api/v1/config/telegram-agent",
      body,
    );
  }

  async getPositionReassessmentConfig(): Promise<PositionReassessmentConfigWire> {
    return this.call<PositionReassessmentConfigWire>(
      "GET",
      "/api/v1/config/position-reassessment",
    );
  }

  async setPositionReassessmentConfig(
    body: PositionReassessmentConfigUpdateBody,
  ): Promise<PositionReassessmentConfigWire> {
    return this.call<PositionReassessmentConfigWire>(
      "PUT",
      "/api/v1/config/position-reassessment",
      body,
    );
  }

  async testTelegramAgentConnection(): Promise<TelegramAgentTestConnectionResponseWire> {
    return this.call<TelegramAgentTestConnectionResponseWire>(
      "POST",
      "/api/v1/config/telegram-agent/test-connection",
      {},
    );
  }

  async testTelegramAgentNotification(): Promise<TelegramAgentTestNotificationResponseWire> {
    return this.call<TelegramAgentTestNotificationResponseWire>(
      "POST",
      "/api/v1/config/telegram-agent/test-notification",
      {},
    );
  }

  async restartTelegramAgent(): Promise<TelegramAgentRestartResponseWire> {
    return this.call<TelegramAgentRestartResponseWire>(
      "POST",
      "/api/v1/config/telegram-agent/restart",
      {},
    );
  }

  // ----- /api/v1/wizard/event (Phase 4.5 D8) -----

  async postWizardEvent(
    body: WizardEventRequest,
  ): Promise<WizardEventResponse> {
    return this.call<WizardEventResponse>(
      "POST",
      "/api/v1/wizard/event",
      body,
    );
  }

  // ----- /api/v1/wizard/configure (Phase 5 D-Live) -----

  async postWizardConfigure(
    body: WizardConfigureRequest,
  ): Promise<WizardConfigureResponse> {
    return this.call<WizardConfigureResponse>(
      "POST",
      "/api/v1/wizard/configure",
      body,
    );
  }

  // ----- /api/v1/wizard/credentials (Phase 5 (d) follow-up) -----

  async postWizardCredentials(
    body: WizardCredentialsRequest,
  ): Promise<WizardCredentialsResponse> {
    return this.call<WizardCredentialsResponse>(
      "POST",
      "/api/v1/wizard/credentials",
      body,
    );
  }

  async postProvisionLiveAccount(
    body: ProvisionLiveAccountRequest,
  ): Promise<ProvisionLiveAccountResponse> {
    return this.call<ProvisionLiveAccountResponse>(
      "POST",
      "/api/v1/accounts/provision-live",
      body,
    );
  }

  async postTestAndProvisionLiveAccount(
    body: TestAndProvisionLiveRequest,
  ): Promise<TestAndProvisionLiveResponse> {
    const path = "/api/v1/accounts/test-and-provision-live";
    const raw = await this.call<unknown>("POST", path, body);
    return validateWire<TestAndProvisionLiveResponse>(
      path,
      testAndProvisionLiveResponseSchema,
      raw,
    );
  }

  async listPendingLiveAccounts(): Promise<PendingLiveAccountsResponse> {
    const path = "/api/v1/accounts/pending-live";
    const raw = await this.call<unknown>("GET", path);
    return validateWire<PendingLiveAccountsResponse>(
      path,
      pendingLiveAccountsResponseSchema,
      raw,
    );
  }

  // ----- read-side endpoints (Phase 5 D-Live Slice 2) -----

  async listRiskLimits(
    brokerAccountId?: string,
  ): Promise<RiskLimitWire[]> {
    const params = new URLSearchParams();
    if (brokerAccountId) {
      params.set("broker_account_id", brokerAccountId);
    }
    const qs = params.toString();
    return this.call<RiskLimitWire[]>(
      "GET",
      `/api/v1/risk/limits${qs ? `?${qs}` : ""}`,
    );
  }

  async getSectorLimits(
    brokerAccountId: string,
  ): Promise<SectorLimitsWire> {
    const params = new URLSearchParams({
      broker_account_id: brokerAccountId,
    });
    return this.call<SectorLimitsWire>(
      "GET",
      `/api/v1/risk/sector-limits?${params.toString()}`,
    );
  }

  async setSectorLimits(
    body: SectorLimitsUpdateBody,
  ): Promise<SectorLimitsWire> {
    return this.call<SectorLimitsWire>(
      "PUT",
      "/api/v1/risk/sector-limits",
      body,
    );
  }

  async listPendingChanges(): Promise<PendingConfigChangeWire[]> {
    return this.call<PendingConfigChangeWire[]>(
      "GET",
      "/api/v1/config/pending-changes",
    );
  }

  async listPendingApprovals(): Promise<PendingApprovalWire[]> {
    const path = "/api/v1/approvals/pending";
    const raw = await this.call<unknown>("GET", path);
    return validateWire<PendingApprovalWire[]>(
      path,
      pendingApprovalsWireSchema,
      raw,
    );
  }

  // ----- /api/v1/signals/generate -----

  async generateSignals(body: {
    resume_research_workflow_run_id?: string;
  } = {}): Promise<SignalsGenerateWireResponse> {
    // POSTs with an empty body — the backend uses the most-recent
    // completed session date when as_of is absent. Returns signal
    // details plus the auto-created approval UUIDs so the caller
    // can show immediate feedback and refresh the pending list.
    return this.call<SignalsGenerateWireResponse>(
      "POST",
      "/api/v1/signals/generate",
      body,
    );
  }

  async listSignalHistory(limit = 100): Promise<SignalsHistoryResponseWire> {
    const params = new URLSearchParams({ limit: String(limit) });
    return this.call<SignalsHistoryResponseWire>(
      "GET",
      `/api/v1/signals/history?${params.toString()}`,
    );
  }

  async listResearchReports(
    limit = 100,
    symbol?: string,
  ): Promise<ResearchReportArchiveResponseWire> {
    const params = new URLSearchParams({ limit: String(limit) });
    const trimmedSymbol = symbol?.trim();
    if (trimmedSymbol) params.set("symbol", trimmedSymbol);
    return this.call<ResearchReportArchiveResponseWire>(
      "GET",
      `/api/v1/research/reports?${params.toString()}`,
    );
  }

  async createResearchReport(args: {
    symbol: string;
    template?: "research_v1" | "deep_dive_v1";
    forceRegenerate?: boolean;
  }): Promise<ResearchReportCreateResponseWire> {
    return this.call<ResearchReportCreateResponseWire>(
      "POST",
      "/api/v1/research/reports",
      {
        symbol: args.symbol,
        template: args.template ?? "research_v1",
        force_regenerate: args.forceRegenerate ?? false,
      },
    );
  }

  async listSavedResearchEvaluations(
    limit = 100,
    symbol?: string,
  ): Promise<SavedResearchEvaluationsResponseWire> {
    const params = new URLSearchParams({ limit: String(limit) });
    const trimmedSymbol = symbol?.trim();
    if (trimmedSymbol) params.set("symbol", trimmedSymbol.toUpperCase());
    return this.call<SavedResearchEvaluationsResponseWire>(
      "GET",
      `/api/v1/research/evaluations/saved?${params.toString()}`,
    );
  }

  async listAccounts(): Promise<AccountWire[]> {
    const path = "/api/v1/accounts";
    const raw = await this.call<unknown>("GET", path);
    return validateWire<AccountWire[]>(path, accountsWireSchema, raw);
  }

  async listPositions(): Promise<PositionWire[]> {
    return this.call<PositionWire[]>("GET", "/api/v1/positions");
  }

  async listClosedStockTrades(args: {
    brokerAccountId?: string;
    symbol?: string;
    from?: string;
    to?: string;
    limit?: number;
  } = {}): Promise<ClosedStockTradesResponseWire> {
    const params = new URLSearchParams();
    if (args.brokerAccountId) {
      params.set("broker_account_id", args.brokerAccountId);
    }
    const symbol = args.symbol?.trim();
    if (symbol) {
      params.set("symbol", symbol.toUpperCase());
    }
    if (args.from) {
      params.set("from", args.from);
    }
    if (args.to) {
      params.set("to", args.to);
    }
    if (args.limit !== undefined) {
      params.set("limit", String(args.limit));
    }
    const suffix = params.toString() ? `?${params.toString()}` : "";
    return this.call<ClosedStockTradesResponseWire>(
      "GET",
      `/api/v1/positions/closed-trades${suffix}`,
    );
  }

  async listPositionTrades(): Promise<PositionTradeWire[]> {
    return this.call<PositionTradeWire[]>("GET", "/api/v1/position-trades");
  }

  async listInvestments(): Promise<InvestmentPositionWire[]> {
    return this.call<InvestmentPositionWire[]>("GET", "/api/v1/investment");
  }

  async listBaskets(): Promise<BasketWire[]> {
    return this.call<BasketWire[]>("GET", "/api/v1/baskets");
  }

  // ----- Phase 5 a.7 — Health-page backup panel -----

  async listBackupTests(
    limit: number = 7,
  ): Promise<BackupTestsResponseWire> {
    const params = new URLSearchParams({ limit: String(limit) });
    return this.call<BackupTestsResponseWire>(
      "GET",
      `/api/v1/system/backup-tests?${params.toString()}`,
    );
  }

  async verifyLatestBackup(): Promise<BackupVerifyResponseWire> {
    return this.call<BackupVerifyResponseWire>(
      "POST",
      "/api/v1/system/backup-verify",
    );
  }

  // ----- /api/v1/system/halt + resume (Phase 5 h — TDD §7.4) -----

  async postSystemHalt(reason: string = "operator_request"): Promise<void> {
    await this.call<{ halted: boolean; reason: string }>(
      "POST",
      "/api/v1/system/halt",
      { reason },
    );
  }

  async postSystemResume(): Promise<void> {
    await this.call<{ halted: boolean }>(
      "POST",
      "/api/v1/system/resume",
      {},
    );
  }

  // ----- /api/v1/events/recent (Phase 5 D) -----

  async getRecentEvents(
    limit: number = 20,
  ): Promise<RecentEventsResponseWire> {
    const params = new URLSearchParams({ limit: String(limit) });
    return this.call<RecentEventsResponseWire>(
      "GET",
      `/api/v1/system/events/recent?${params.toString()}`,
    );
  }

  // ----- /api/v1/charts/* -----

  async listChartSymbols(): Promise<ChartSymbolWire[]> {
    return this.call<ChartSymbolWire[]>("GET", "/api/v1/charts/symbols");
  }

  async getChartSnapshot(
    symbol: string,
    lookback: number = 260,
    timeframe: ChartTimeframeWire = "1D",
  ): Promise<ChartSnapshotWire> {
    const params = new URLSearchParams({
      timeframe,
      lookback: String(lookback),
    });
    return this.call<ChartSnapshotWire>(
      "GET",
      `/api/v1/charts/${encodeURIComponent(symbol)}?${params.toString()}`,
    );
  }

  // ----- /api/v1/positions/* — Open-Position Management (Phase 5 d D1/D4) -----

  async getLivePnL(): Promise<LivePnLResponseWire> {
    return this.call<LivePnLResponseWire>(
      "GET",
      "/api/v1/positions/live-pnl",
    );
  }

  async runPositionReassessment(args: {
    brokerAccountId?: string;
    symbol?: string;
    includeEquity?: boolean;
    includeCrypto?: boolean;
    includeForex?: boolean;
    persist?: boolean;
  } = {}): Promise<PositionReassessmentRunResponseWire> {
    return this.call<PositionReassessmentRunResponseWire>(
      "POST",
      "/api/v1/positions/reassess",
      {
        broker_account_id: args.brokerAccountId ?? null,
        symbol: args.symbol ?? null,
        include_equity: args.includeEquity ?? true,
        include_crypto: args.includeCrypto ?? true,
        include_forex: args.includeForex ?? true,
        persist: args.persist ?? true,
      },
    );
  }

  async listPositionReassessments(args: {
    brokerAccountId?: string;
    symbol?: string;
    status?: "active" | "dismissed" | "confirmed" | "expired" | null;
    limit?: number;
  } = {}): Promise<PositionReassessmentListResponseWire> {
    const params = new URLSearchParams();
    if (args.brokerAccountId) {
      params.set("broker_account_id", args.brokerAccountId);
    }
    const symbol = args.symbol?.trim();
    if (symbol) {
      params.set("symbol", symbol.toUpperCase());
    }
    if (args.status !== undefined) {
      if (args.status !== null) params.set("status", args.status);
    }
    if (args.limit) {
      params.set("limit", String(args.limit));
    }
    const qs = params.toString();
    return this.call<PositionReassessmentListResponseWire>(
      "GET",
      `/api/v1/positions/reassessments${qs ? `?${qs}` : ""}`,
    );
  }

  async confirmPositionReassessmentAction(
    reassessmentId: string,
    idempotencyKey: string,
  ): Promise<PositionReassessmentWire> {
    return this.call<PositionReassessmentWire>(
      "POST",
      `/api/v1/positions/reassessments/${encodeURIComponent(reassessmentId)}/confirm-action`,
      { idempotency_key: idempotencyKey },
    );
  }

  async dismissPositionReassessment(
    reassessmentId: string,
    note?: string | null,
  ): Promise<PositionReassessmentWire> {
    return this.call<PositionReassessmentWire>(
      "POST",
      `/api/v1/positions/reassessments/${encodeURIComponent(reassessmentId)}/dismiss`,
      { note: note ?? null },
    );
  }

  async tightenStop(
    brokerAccountId: string,
    symbol: string,
    body: TightenStopRequestBody,
  ): Promise<TightenStopResponseWire> {
    return this.call<TightenStopResponseWire>(
      "POST",
      `/api/v1/positions/${encodeURIComponent(brokerAccountId)}/${encodeURIComponent(symbol)}/tighten-stop`,
      body,
    );
  }

  async adjustTarget(
    brokerAccountId: string,
    symbol: string,
    body: AdjustTargetRequestBody,
  ): Promise<AdjustTargetResponseWire> {
    return this.call<AdjustTargetResponseWire>(
      "POST",
      `/api/v1/positions/${encodeURIComponent(brokerAccountId)}/${encodeURIComponent(symbol)}/adjust-target`,
      body,
    );
  }

  async breakEven(
    brokerAccountId: string,
    symbol: string,
    body: PositionActionIdempotentRequestBody,
  ): Promise<TightenStopResponseWire> {
    return this.call<TightenStopResponseWire>(
      "POST",
      `/api/v1/positions/${encodeURIComponent(brokerAccountId)}/${encodeURIComponent(symbol)}/break-even`,
      body,
    );
  }

  async exitAtMarket(
    brokerAccountId: string,
    symbol: string,
    body: PositionActionIdempotentRequestBody,
  ): Promise<ExitAtMarketResponseWire> {
    return this.call<ExitAtMarketResponseWire>(
      "POST",
      `/api/v1/positions/${encodeURIComponent(brokerAccountId)}/${encodeURIComponent(symbol)}/exit-at-market`,
      body,
    );
  }

  async adjustPositionQty(
    brokerAccountId: string,
    symbol: string,
    body: PositionAdjustQtyRequestBody,
  ): Promise<PositionAdjustQtyResponseWire> {
    return this.call<PositionAdjustQtyResponseWire>(
      "POST",
      `/api/v1/positions/${encodeURIComponent(brokerAccountId)}/${encodeURIComponent(symbol)}/adjust-qty`,
      body,
    );
  }

  async exitAtLimit(
    brokerAccountId: string,
    symbol: string,
    body: ExitAtLimitRequestBody,
  ): Promise<ExitAtLimitResponseWire> {
    return this.call<ExitAtLimitResponseWire>(
      "POST",
      `/api/v1/positions/${encodeURIComponent(brokerAccountId)}/${encodeURIComponent(symbol)}/exit-limit`,
      body,
    );
  }

  // ----- /api/v1/system/activation-status (Phase 5 D-Live) -----

  async getSystemActivationStatus(): Promise<SystemActivationStatusResponse> {
    return this.call<SystemActivationStatusResponse>(
      "GET",
      "/api/v1/system/activation-status",
    );
  }

  // ----- /api/v1/disclosures/status (Phase 4.5 D8 / PRD §6.7) -----

  async getDisclosuresStatus(
    currentHash: string,
  ): Promise<DisclosuresStatusResponse> {
    const params = new URLSearchParams({
      current_hash: currentHash,
    });
    return this.call<DisclosuresStatusResponse>(
      "GET",
      `/api/v1/disclosures/status?${params.toString()}`,
    );
  }

  // ----- /api/v1/system/ai-cost (Phase 7a D8) -----

  async getAiCostStatus(): Promise<AiCostStatusWire> {
    return this.call<AiCostStatusWire>("GET", "/api/v1/system/ai-cost");
  }

  // ----- /api/v1/system/crypto-paper-validation/* (Phase 8f) -----

  async getCryptoPaperValidationStatus(params: {
    startDate: string;
    endDate: string;
    operatorAttestedMostlyProfitable?: boolean;
  }): Promise<CryptoPaperValidationGateResponse> {
    const query = new URLSearchParams({
      start_date: params.startDate,
      end_date: params.endDate,
      operator_attested_mostly_profitable: String(
        params.operatorAttestedMostlyProfitable ?? false,
      ),
    });
    return this.call<CryptoPaperValidationGateResponse>(
      "GET",
      `/api/v1/system/crypto-paper-validation/status?${query.toString()}`,
    );
  }

  async postCryptoPaperValidationDay(
    body: CryptoPaperValidationDayRequest,
  ): Promise<CryptoPaperValidationDayResponse> {
    return this.call<CryptoPaperValidationDayResponse>(
      "POST",
      "/api/v1/system/crypto-paper-validation/days",
      body,
    );
  }

  async listCryptoStrategyValidationRuns(options?: {
    strategyId?: string;
    limit?: number;
  }): Promise<CryptoValidationRunListResponseWire> {
    const params = new URLSearchParams({
      limit: String(options?.limit ?? 20),
    });
    const strategyId = options?.strategyId?.trim();
    if (strategyId) params.set("strategy_id", strategyId);
    return this.call<CryptoValidationRunListResponseWire>(
      "GET",
      `/api/v1/system/crypto-strategy-validation/runs?${params.toString()}`,
    );
  }

  async getCryptoStrategyValidationRun(
    validationRunId: string,
  ): Promise<CryptoValidationRunWire> {
    return this.call<CryptoValidationRunWire>(
      "GET",
      `/api/v1/system/crypto-strategy-validation/runs/${encodeURIComponent(
        validationRunId,
      )}`,
    );
  }

  async getCryptoPaperConfig(): Promise<CryptoPaperTradingConfigWire> {
    return this.call<CryptoPaperTradingConfigWire>(
      "GET",
      "/api/v1/config/crypto-paper",
    );
  }

  async getCryptoExchangeCapabilities(): Promise<CryptoExchangeCapabilityListResponseWire> {
    return this.call<CryptoExchangeCapabilityListResponseWire>(
      "GET",
      "/api/v1/config/crypto-exchanges",
    );
  }

  async putCryptoPaperConfig(
    body: CryptoPaperTradingConfigUpdateWire,
  ): Promise<CryptoPaperTradingConfigWire> {
    return this.call<CryptoPaperTradingConfigWire>(
      "PUT",
      "/api/v1/config/crypto-paper",
      body,
    );
  }

  // ----- /api/v1/config/forex + /api/v1/forex (Phase 9-UI) -----

  async getForexConfig(): Promise<ForexConfigWire> {
    return this.call<ForexConfigWire>("GET", "/api/v1/config/forex");
  }

  async getForexPairs(): Promise<ForexPairsResponseWire> {
    return this.call<ForexPairsResponseWire>("GET", "/api/v1/forex/pairs");
  }

  async getForexAccount(): Promise<ForexAccountWire> {
    return this.call<ForexAccountWire>("GET", "/api/v1/forex/account");
  }

  // Registers the tastyfx DEMO account as a DISABLED broker_accounts
  // row. Idempotent; enables nothing (registry filters enabled=TRUE,
  // and the DB CHECK makes an enabled/live tastyfx row
  // unrepresentable until Phase 9b).
  async registerForexAccount(
    body: ForexAccountRegisterRequest,
  ): Promise<ForexAccountWire> {
    return this.call<ForexAccountWire>("POST", "/api/v1/forex/account", body);
  }

  // ----- /api/v1/crypto/watchlist-analysis -----

  async getCryptoWatchlistAnalysis(): Promise<CryptoWatchlistAnalysisResponseWire> {
    return this.call<CryptoWatchlistAnalysisResponseWire>(
      "GET",
      "/api/v1/crypto/watchlist-analysis",
    );
  }

  async getMoonshotSignals(
    args: MoonshotSignalsRequest = {},
  ): Promise<MoonshotSignalsResponseWire> {
    const params = new URLSearchParams();
    if (args.asOf) {
      params.set("as_of", args.asOf);
    }
    if (args.universe) {
      params.set("universe", args.universe);
    }
    if (args.limit !== undefined) {
      params.set("limit", String(args.limit));
    }
    if (args.maxPrice !== undefined) {
      params.set("max_price", String(args.maxPrice));
    }
    if (args.forceRefresh) {
      params.set("refresh", "true");
    }
    const suffix = params.size > 0 ? `?${params.toString()}` : "";
    return this.call<MoonshotSignalsResponseWire>(
      "GET",
      `/api/v1/moonshots${suffix}`,
    );
  }

  async rankMoonshotTopTen(
    args: MoonshotSignalsRequest = {},
  ): Promise<MoonshotAIRankResponseWire> {
    return this.call<MoonshotAIRankResponseWire>(
      "POST",
      "/api/v1/moonshots/ai-rank",
      {
        as_of: args.asOf ?? null,
        universe: args.universe ?? "SP500",
        limit: args.limit ?? 50,
        max_price: args.maxPrice ?? 20,
        force_refresh: Boolean(args.forceRefresh),
      },
    );
  }

  async getCryptoPaperEngineStatus(): Promise<CryptoPaperEngineStatusWire> {
    return this.call<CryptoPaperEngineStatusWire>(
      "GET",
      "/api/v1/crypto/paper-engine-status",
    );
  }

  async getCryptoPaperTradeHistory(
    limit: number = 50,
  ): Promise<CryptoPaperTradeHistoryResponseWire> {
    const params = new URLSearchParams({ limit: String(limit) });
    return this.call<CryptoPaperTradeHistoryResponseWire>(
      "GET",
      `/api/v1/crypto/paper-trade-history?${params.toString()}`,
    );
  }

  async generateCryptoSignals(): Promise<CryptoSignalsGenerateResponseWire> {
    return this.call<CryptoSignalsGenerateResponseWire>(
      "POST",
      "/api/v1/crypto/signals/generate",
      {},
    );
  }

  async runCryptoPaperEngine(options?: {
    waitSeconds?: number;
    asyncRun?: boolean;
  }): Promise<CryptoPaperEngineRunResponseWire> {
    const params = new URLSearchParams();
    if ((options?.waitSeconds ?? 0) > 0) {
      params.set("wait_seconds", String(options?.waitSeconds));
    }
    if (options?.asyncRun) {
      params.set("async_run", "true");
    }
    const suffix = params.size > 0 ? `?${params.toString()}` : "";
    return this.call<CryptoPaperEngineRunResponseWire>(
      "POST",
      `/api/v1/crypto/paper-engine/run${suffix}`,
      {},
    );
  }

  // ----- Research Workbench -----

  async evaluateSymbol(
    symbol: string,
    horizon: "day_swing" | "investment" | "both",
    asOf?: string,
  ): Promise<EvaluationResultWire> {
    return this.call<EvaluationResultWire>(
      "POST",
      "/api/v1/research/evaluate",
      { symbol, horizon, as_of: asOf ?? null },
    );
  }

  async acceptEvaluation(
    evaluationId: string,
  ): Promise<AcceptEvaluationResponseWire> {
    return this.call<AcceptEvaluationResponseWire>(
      "POST",
      `/api/v1/research/evaluations/${evaluationId}/accept`,
    );
  }

  async createApprovalFromEvaluation(
    body: CreateApprovalFromEvalRequestBody,
  ): Promise<CreateApprovalFromEvalResponseWire> {
    return this.call<CreateApprovalFromEvalResponseWire>(
      "POST",
      `/api/v1/research/evaluations/${body.evaluation_id}/create-approval`,
      body,
    );
  }

  async runAgentReview(
    evaluationId: string,
    includeSocialSentiment: boolean,
    resumeWorkflowRunId?: string,
  ): Promise<RunAgentReviewResponseWire> {
    return this.call<RunAgentReviewResponseWire>(
      "POST",
      `/api/v1/research/evaluations/${evaluationId}/agent-review`,
      {
        include_social_sentiment: includeSocialSentiment,
        resume_workflow_run_id: resumeWorkflowRunId ?? null,
      },
    );
  }

  async getAgentReview(
    evaluationId: string,
  ): Promise<MultiAgentReviewResultWire> {
    return this.call<MultiAgentReviewResultWire>(
      "GET",
      `/api/v1/research/evaluations/${evaluationId}/agent-review`,
    );
  }

  async runShadowAgentReviews(body: {
    symbol?: string;
    limit?: number;
    accepted_only?: boolean;
    include_social_sentiment?: boolean;
    resume_workflow_run_id?: string;
  }): Promise<ShadowAgentReviewRunResponseWire> {
    return this.call<ShadowAgentReviewRunResponseWire>(
      "POST",
      "/api/v1/research/agent-reviews/shadow-run",
      body,
    );
  }

  async getSocialSentiment(
    symbol: string,
    refresh = false,
  ): Promise<SocialSentimentSnapshotWire> {
    const suffix = refresh ? "?refresh=true" : "";
    return this.call<SocialSentimentSnapshotWire>(
      "GET",
      `/api/v1/research/social-sentiment/${symbol}${suffix}`,
    );
  }

  async listDecisionReflections(
    limit = 20,
    symbol?: string,
  ): Promise<DecisionReflectionsResponseWire> {
    const params = new URLSearchParams({ limit: String(limit) });
    if (symbol && symbol.trim() !== "") {
      params.set("symbol", symbol.trim().toUpperCase());
    }
    return this.call<DecisionReflectionsResponseWire>(
      "GET",
      `/api/v1/research/reflections?${params.toString()}`,
    );
  }

  async generateDecisionReflections(body: {
    symbol?: string;
    holding_window_days?: number;
    benchmark_symbol?: string;
    limit?: number;
    accepted_only?: boolean;
    source_types?: string[];
  }): Promise<DecisionReflectionsResponseWire> {
    return this.call<DecisionReflectionsResponseWire>(
      "POST",
      "/api/v1/research/reflections/generate",
      body,
    );
  }

  async getWorkflowStatus(
    workflowRunId: string,
  ): Promise<AIWorkflowStatusResponseWire> {
    return this.call<AIWorkflowStatusResponseWire>(
      "GET",
      `/api/v1/research/workflows/${workflowRunId}`,
    );
  }

  async getLatestWorkflowStatus(params: {
    workflow_type: string;
    symbol?: string;
    feature: string;
    as_of_date?: string;
  }): Promise<AIWorkflowStatusResponseWire> {
    const query = new URLSearchParams({
      workflow_type: params.workflow_type,
      symbol: params.symbol ?? "*",
      feature: params.feature,
    });
    if (params.as_of_date) {
      query.set("as_of_date", params.as_of_date);
    }
    return this.call<AIWorkflowStatusResponseWire>(
      "GET",
      `/api/v1/research/workflows/latest?${query.toString()}`,
    );
  }

  async listAIProviderConfigs(): Promise<AIProviderConfigListResponseWire> {
    return this.call<AIProviderConfigListResponseWire>(
      "GET",
      "/api/v1/config/ai-providers",
    );
  }

  async putAIProviderConfig(
    feature: string,
    body: AIProviderConfigWire,
  ): Promise<AIProviderConfigWire> {
    return this.call<AIProviderConfigWire>(
      "PUT",
      `/api/v1/config/ai-providers/${feature}`,
      body,
    );
  }

  async getAIProviderPreflight(): Promise<AIProviderPreflightResponseWire> {
    return this.call<AIProviderPreflightResponseWire>(
      "GET",
      "/api/v1/config/ai-provider-preflight",
    );
  }

  async resetAIBreaker(
    feature: string,
    reason: string,
  ): Promise<AIBreakerResetResponseWire> {
    return this.call<AIBreakerResetResponseWire>(
      "POST",
      `/api/v1/config/ai-breakers/${feature}/reset`,
      { reason },
    );
  }

  // ----- core call helper -----

  private async call<T>(
    method: "GET" | "POST" | "PUT" | "PATCH",
    path: string,
    body?: unknown,
    opts?: {
      // Additional HTTP status codes beyond 2xx that should be treated
      // as successful responses and parsed as T instead of thrown as
      // ProblemDetailError.  Used by getHealth() which intentionally
      // returns 503 when the aggregate subsystem status is "down" —
      // both 200 and 503 carry a valid HealthResponse body.
      acceptStatuses?: number[];
    },
  ): Promise<T> {
    const url = `${this.baseUrl}${path}`;
    const headers: Record<string, string> = {
      Accept: "application/json",
    };
    let init: RequestInit = {
      method,
      headers,
      // Same-origin loopback; cookies flow automatically.
      credentials: "include",
    };
    if (body !== undefined) {
      headers["Content-Type"] = "application/json";
      init = { ...init, body: JSON.stringify(body) };
    }

    const response = await this.fetchImpl(url, init);

    // A non-2xx status is treated as a successful (accepted) response only
    // when two conditions are both met:
    //  1. The status code appears in opts.acceptStatuses (caller opt-in), AND
    //  2. The response Content-Type is application/json.
    //
    // Condition (2) guards against the case where a load-balancer or reverse
    // proxy (e.g. nginx) returns a plain-HTML error page with the same status
    // code. Treating HTML as a parseable T would throw a SyntaxError from
    // JSON.parse; falling through to the error path instead synthesises a
    // clean ProblemDetailError("HTTP 503") that the UI can display.
    const contentTypeIsJson =
      response.headers.get("Content-Type")?.includes("application/json") ??
      false;
    const isAccepted =
      response.ok ||
      ((opts?.acceptStatuses?.includes(response.status) ?? false) &&
        contentTypeIsJson);

    if (!isAccepted) {
      // Try to parse RFC 7807 problem-details. If parsing fails,
      // synthesize a generic problem with the HTTP status.
      let problem: ProblemDetail;
      try {
        const parsed = (await response.json()) as Partial<ProblemDetail>;
        problem = {
          type:
            parsed.type ?? "https://tradingplatform.local/errors/unknown",
          title: parsed.title ?? "Unknown Error",
          status: parsed.status ?? response.status,
          detail: parsed.detail ?? "",
          instance: parsed.instance ?? path,
        };
      } catch {
        problem = {
          type: "https://tradingplatform.local/errors/unknown",
          title: `HTTP ${response.status}`,
          status: response.status,
          detail: "",
          instance: path,
        };
      }
      // 401 interceptor: route back to login. We never invoke
      // the hook for the login endpoint itself (a 401 there is
      // a credential failure that the form should display
      // inline, not a stale-session redirect).
      if (
        response.status === 401 &&
        this.onUnauthorized &&
        !path.endsWith("/api/v1/login")
      ) {
        this.onUnauthorized();
      }
      throw new ProblemDetailError(problem);
    }

    // Some endpoints (logout) return 200 with empty body.
    const text = await response.text();
    if (text.length === 0) {
      return {} as T;
    }
    // NOTE: this cast is unchecked. Money-path endpoints (pending
    // approvals, trade preview/review, confirm-and-place, accounts)
    // re-validate the parsed value with zod via validateWire() at
    // their call sites — see "Runtime response validation" above for
    // how to extend that pattern to more endpoints.
    return JSON.parse(text) as T;
  }
}
